import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { useTaskStore } from './taskStore'
import { useSettingsStore } from './settingsStore'
import { useWeatherStore } from './weatherStore'
import { getCompletionLogsByRange } from '@/services/database'

export type ThemeOverlayType =
  | 'none' | 'survival' | 'victory-gold' | 'aurora'
  | 'weather-clear' | 'weather-cloudy' | 'weather-rain' | 'weather-snow' | 'weather-storm'

interface TimeCapsuleData {
  historicalDate: string
  completionCount: number
  taskTitles: string[]
}

interface UnlockedTheme {
  themeId: 'aurora'
  unlockedAt: string
  expiresAt: string
  milestone: number
  categoryId: number
}

interface ThemeOrchestratorState {
  // Computed overlay (NOT persisted)
  activeOverlay: ThemeOverlayType

  // Survival mode
  survivalDismissed: boolean

  // Time capsule
  timeCapsuleCheckedDate: string | null
  timeCapsuleData: TimeCapsuleData | null
  timeCapsuleDismissed: boolean

  // Gamification
  categoryStreaks: Record<string, { currentStreak: number; lastCompletionDate: string }>
  unlockedThemes: UnlockedTheme[]
  gamificationDismissed: boolean

  // Actions
  initialize: () => Promise<void>
  resolveOverlay: () => void
  dismissSurvival: () => void
  dismissTimeCapsule: () => void
  dismissGamification: () => void
  checkTimeCapsule: () => Promise<void>
  updateCategoryStreak: (categoryId: number) => void
  cleanupExpiredThemes: () => void
}

const SURVIVAL_THRESHOLD = 5
const TIME_CAPSULE_MIN_COMPLETIONS = 3
const STREAK_MILESTONES: Record<number, { themeId: 'aurora'; durationDays: number }> = {
  30: { themeId: 'aurora', durationDays: 7 },
}

function getTodayStr(): string {
  // Local timezone — toISOString is UTC and rolls over off by ±9h for KST users.
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export const useThemeOrchestratorStore = create<ThemeOrchestratorState>()(
  persist(
    (set, get) => ({
      activeOverlay: 'none',
      survivalDismissed: false,
      timeCapsuleCheckedDate: null,
      timeCapsuleData: null,
      timeCapsuleDismissed: false,
      categoryStreaks: {},
      unlockedThemes: [],
      gamificationDismissed: false,

      initialize: async () => {
        get().cleanupExpiredThemes()
        await get().checkTimeCapsule()
        get().resolveOverlay()
      },

      resolveOverlay: () => {
        const settings = useSettingsStore.getState().settings
        const tasks = useTaskStore.getState().tasks
        const state = get()
        const today = getTodayStr()

        const setOverlay = (next: ThemeOverlayType) => {
          if (state.activeOverlay !== next) set({ activeOverlay: next })
        }

        // Priority 1: Manual override — user has explicitly set a palette, skip all
        // (handled externally — if user changes palette, we don't override)

        // Priority 2: Survival Mode
        if (settings.survivalModeEnabled && !state.survivalDismissed) {
          const overdueHighCount = tasks.filter(
            (t) =>
              t.status === 'pending' &&
              t.priority === 'high' &&
              t.dueDate != null &&
              t.dueDate < today
          ).length

          if (overdueHighCount >= SURVIVAL_THRESHOLD) {
            setOverlay('survival')
            return
          }
          // If was in survival but now below threshold, reset dismiss
          if (state.survivalDismissed) {
            set({ survivalDismissed: false })
          }
        }

        // Priority 3: Time Capsule (Victory Gold)
        if (settings.timeCapsuleEnabled && !state.timeCapsuleDismissed && state.timeCapsuleData) {
          setOverlay('victory-gold')
          return
        }

        // Priority 4: Gamification (Aurora)
        if (settings.gamificationEnabled && !state.gamificationDismissed) {
          const activeReward = state.unlockedThemes.find((t) => new Date(t.expiresAt).getTime() > Date.now())
          if (activeReward) {
            setOverlay(activeReward.themeId)
            return
          }
        }

        // Priority 4.5: Weather Theme
        if (settings.weatherThemeEnabled) {
          const weatherData = useWeatherStore.getState().weatherData
          if (weatherData) {
            setOverlay(`weather-${weatherData.condition}` as ThemeOverlayType)
            return
          }
        }

        // Priority 5: Default — no overlay
        setOverlay('none')
      },

      dismissSurvival: () => {
        set({ survivalDismissed: true, activeOverlay: 'none' })
        // Re-resolve after survival dismissed
        setTimeout(() => get().resolveOverlay(), 0)
      },

      dismissTimeCapsule: () => {
        set({ timeCapsuleDismissed: true })
        get().resolveOverlay()
      },

      dismissGamification: () => {
        set({ gamificationDismissed: true })
        get().resolveOverlay()
      },

      checkTimeCapsule: async () => {
        const today = getTodayStr()
        const state = get()

        if (state.timeCapsuleCheckedDate === today) return

        try {
          const oneYearAgo = new Date()
          oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1)
          const oneYearAgoStr = `${oneYearAgo.getFullYear()}-${String(oneYearAgo.getMonth() + 1).padStart(2, '0')}-${String(oneYearAgo.getDate()).padStart(2, '0')}`

          const logs = await getCompletionLogsByRange(oneYearAgoStr, oneYearAgoStr)

          if (logs.length >= TIME_CAPSULE_MIN_COMPLETIONS) {
            set({
              timeCapsuleCheckedDate: today,
              timeCapsuleDismissed: false,
              timeCapsuleData: {
                historicalDate: oneYearAgoStr,
                completionCount: logs.length,
                taskTitles: logs.slice(0, 5).map((l) => `Task #${l.taskId}`),
              },
            })
          } else {
            set({
              timeCapsuleCheckedDate: today,
              timeCapsuleData: null,
              timeCapsuleDismissed: false,
            })
          }
        } catch {
          set({ timeCapsuleCheckedDate: today, timeCapsuleData: null })
        }
      },

      updateCategoryStreak: (categoryId: number) => {
        const state = get()
        const today = getTodayStr()
        const key = String(categoryId)
        const current = state.categoryStreaks[key]

        let newStreak: number

        if (!current) {
          newStreak = 1
        } else if (current.lastCompletionDate === today) {
          // Already counted today
          return
        } else {
          const lastDate = new Date(current.lastCompletionDate)
          const todayDate = new Date(today)
          const diffDays = Math.floor(
            (todayDate.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24)
          )

          newStreak = diffDays === 1 ? current.currentStreak + 1 : 1
        }

        const updatedStreaks = {
          ...state.categoryStreaks,
          [key]: { currentStreak: newStreak, lastCompletionDate: today },
        }

        // Check milestones
        const milestone = STREAK_MILESTONES[newStreak]
        let updatedThemes = state.unlockedThemes

        if (milestone) {
          const alreadyUnlocked = state.unlockedThemes.some(
            (t) => t.milestone === newStreak && t.categoryId === categoryId
          )

          if (!alreadyUnlocked) {
            const now = new Date()
            const expires = new Date(now.getTime() + milestone.durationDays * 24 * 60 * 60 * 1000)
            updatedThemes = [
              ...state.unlockedThemes,
              {
                themeId: milestone.themeId,
                unlockedAt: now.toISOString(),
                expiresAt: expires.toISOString(),
                milestone: newStreak,
                categoryId,
              },
            ]
          }
        }

        set({
          categoryStreaks: updatedStreaks,
          unlockedThemes: updatedThemes,
          gamificationDismissed: false,
        })

        get().resolveOverlay()
      },

      cleanupExpiredThemes: () => {
        const now = new Date().toISOString()
        set((state) => ({
          unlockedThemes: state.unlockedThemes.filter((t) => t.expiresAt > now),
        }))
      },
    }),
    {
      name: 'theme-orchestrator',
      partialize: (state) => ({
        survivalDismissed: state.survivalDismissed,
        timeCapsuleCheckedDate: state.timeCapsuleCheckedDate,
        timeCapsuleData: state.timeCapsuleData,
        timeCapsuleDismissed: state.timeCapsuleDismissed,
        categoryStreaks: state.categoryStreaks,
        unlockedThemes: state.unlockedThemes,
        gamificationDismissed: state.gamificationDismissed,
      }),
    }
  )
)
